import { tracker } from 'app-components/utils/core';
import { createWeakMapSelector } from 'app-components/utils/core/reselect';
import type RootState from 'app-components/utils/redux-store/rootState';
import {
  type Collection,
  List as ImmutableList,
  Map as ImmutableMap,
  OrderedMap,
  Record,
  type RecordOf,
  Set,
  isImmutable,
  merge,
} from 'immutable';
import _memoize from 'lodash/memoize';
import { DateTime } from 'luxon';
import { LruObjectCache, createCachedSelector } from 're-reselect';
import { createSelector as createSelectorBase, lruMemoize } from 'reselect';

// other stores
import { type ACCOUNTS_REDUCER_KEY, accountsSelectors } from '../accounts';
import { type CATEGORIES_REDUCER_KEY, categoriesSelectors, categoriesTypes } from '../categories';
import { chartOfAccountsSelectors, chartOfAccountsTypes, chartOfAccountsUtils } from '../chart-of-accounts';
import { selectorsHelpers } from '../core';
import { type DATE_REDUCER_KEY, dateSelectors } from '../date';
import { type FEATURE_FLAGS_REDUCER_KEY, featureFlagsSelectors } from '../feature-flags';
import { type FILTERS_REDUCER_KEY, filtersSelectors, filtersTypes, filtersUtils } from '../filters';
import { type Goal, goalsSelectors, goalsUtils } from '../goals';
import type { SECURITIES_REDUCER_KEY } from '../securities/securitiesTypes';
import { transactionsSelectors, transactionsUtils } from '../transactions';
import {
  SplitItem,
  SplitItemProps,
  TRANSACTIONS_REDUCER_KEY,
  TransactionItem,
  TransactionItemProps,
  TransactionProps,
  TransactionRecord,
  mkTransactionItem,
} from '../transactions/transactionsTypes';

// self
import {
  FREE_TO_SPEND_REDUCER_KEY,
  type FreeToSpend,
  type PlannedSpendingItem,
  type PlannedSpendingItems,
  mkFreeToSpend,
  validRolloverStates,
} from './freeToSpendTypes';

const emptyPlannedList = ImmutableList<PlannedSpendingItem>();
const emptyTxnList = ImmutableList<TransactionRecord>();
const emptyItemsList = ImmutableList<TransactionItem>();
const emptyStringList = ImmutableList<string>();
const emptyStringSet = Set<string>();
const emptyGoalsList = ImmutableList<Goal>();

type TxnList = ImmutableList<TransactionRecord>;
type TxnsCollection = Collection<string | number, TransactionProps>;
type State = Pick<
  RootState,
  | typeof FREE_TO_SPEND_REDUCER_KEY
  | typeof TRANSACTIONS_REDUCER_KEY
  | typeof ACCOUNTS_REDUCER_KEY
  | typeof CATEGORIES_REDUCER_KEY
  | typeof FEATURE_FLAGS_REDUCER_KEY
  | typeof SECURITIES_REDUCER_KEY
  | typeof DATE_REDUCER_KEY
  | typeof FILTERS_REDUCER_KEY
>;

// ======UTILS========================================

const roundOff = (num: number, places: number) => {
  const x = Math.pow(10, places);
  return Math.round(num * x) / x;
};

const getItemsSum = (txnItems?: TransactionItemProps[]) =>
  roundOff(txnItems?.reduce((acc, item) => acc + (item?.item?.amount || 0), 0) || 0, 2);

const getTxnSum = (txns?: TxnsCollection) => roundOff(txns?.reduce((acc, txn) => acc + (txn.amount || 0), 0) || 0, 2);

const getTxnSumForCoa = (txns?: TxnsCollection, coaIds?: Set<string>, negator = true): number =>
  txns?.reduce((acc, txn) => {
    let amount = txn.amount || 0;
    if (txn?.split?.items && isImmutable(txn.split.items) && txn?.split?.items.size > 0 && coaIds) {
      amount = txn.split.items.reduce(
        (accumulator, item: any) => (negator === coaIds.has(item.coa.id) ? accumulator + item.amount : accumulator),
        0,
      );
    }
    return roundOff(acc + amount, 2);
  }, 0) || 0;

const getTxnSumWithCallback = (txns: TxnList, customCallback: any, negator = true) =>
  txns?.reduce((acc, txn) => {
    let amount = txn.amount || 0;
    if (txn?.split?.items && isImmutable(txn.split.items) && txn?.split?.items.size > 0) {
      amount = txn.split.items.reduce(
        (accumulator, item) => (negator === customCallback(item) ? accumulator + item.amount : accumulator),
        0,
      );
    }
    return roundOff(acc + amount, 2);
  }, 0) || 0;

const transferCallback = (item: TransactionRecord) => transactionsUtils.isTransferOrCreditCardPaymentTxn(item);
const transferAndCoaCallback = (item: TransactionProps, coaIds: Set<string>) =>
  transactionsUtils.isTransferOrCreditCardPaymentTxn(item) || (item.coa?.id && coaIds.includes(item.coa.id));
const getValueFromFilterAndTransactions = ({
  filters,
  transactions,
  filterId,
  date,
}: { filters: any; transactions: any; filterId: string; date: string }) => {
  const filterObject = filtersUtils.convertQCSFilterToFilterObject(filters.get(filterId));
  const txns = transactionsUtils.filterTransactionsByFilterObject(
    transactions,
    filterObject.merge({
      dateStart: date,
      dateEnd: DateTime.fromISO(date).endOf('month').toISODate(),
      advanced: filtersTypes.AdvancedFilterObject({ isBillOrSubscription: false, isExcludedFromF2S: false }),
    }),
  );
  const coaIdMap = filters.get(filterId)?.filterItems?.[0]?.chartOfAccounts?.map((coa: any) => coa?.id);
  return getTxnSumForCoa(Array.isArray(txns) ? ImmutableList(txns) : txns, Set(coaIdMap));
};

// ======UTILS=========END===============================

const createSelector = createSelectorBase.withTypes<State>();

const getStore = (state: State) => state[FREE_TO_SPEND_REDUCER_KEY];

export const getLoadPending = (state: State) => getStore(state).loadPending;
export const getIsLoading = (state: State) => getStore(state).isLoading;
export const getError = (state: State) => getStore(state).error;
export const getResourcesById = (state: State) => getStore(state).resourcesById;

export const getLastSyncDate = (state: State) => getStore(state).lastSyncDate;
export const getFreeToSpendById = (state: State, id: string) => getStore(state).resourcesById.get(id);
export const getFreeToSpendForDate = createSelector(
  [getResourcesById, (_state, date: string) => date],
  (resources, date) => resources.find((F2S) => F2S.date === date) || mkFreeToSpend(),
);

const getTransactionsForMonthWithGoals = createSelector(
  [
    (_state, date: string) => date,
    (state) => transactionsSelectors.getTransactionsOfTypes(state, transactionsSelectors.txnTypesCashAndPlanned),
    accountsSelectors.getLoanAccountIds,
  ],
  (date, transactions: ImmutableMap<string, TransactionRecord>, loanAccountIds: Set<string>) => {
    if (!date || !transactions) {
      return emptyTxnList;
    }
    const ftsMonthYear = date.slice(0, -3);
    // Filter down the list to what we need.
    return transactions
      ?.filter((txn) => txn?.postedOn?.startsWith(ftsMonthYear) && txn.accountId && !loanAccountIds?.has(txn.accountId))
      ?.sort((a, b) => (a.postedOn > b.postedOn ? 1 : -1))
      ?.toList();
  },
);

const getTransactionsForMonth = createSelector(
  [getTransactionsForMonthWithGoals, accountsSelectors.getGoalAccountIds],
  (transactions, goalAccountIds) => transactions.filter((txn) => !goalAccountIds.has(txn.accountId as string)),
);

// Motivation - want to get the transactions for a specific coa within a certain date range (usually 6 months) so that we can take the average
export const getTransactionsForCoa = createSelector(
  [
    (state) =>
      accountsSelectors.getLoadPending(state) ||
      categoriesSelectors.getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state),
    transactionsSelectors.getTransactionsByAccountId,
    categoriesSelectors.getCategoriesById,
    (_, props) => props.coaId,
    (_, props) => props.pastCutoff,
    (_, props) => props.currCutoff,
  ],
  (isLoading, txnsByAccountId, categories, coaId, pastCutoff, currCutoff) => {
    if (isLoading || !coaId) {
      return null;
    }
    const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
      'planned-spending-calculation',
      { txnsByAccountId, categories, coaId, pastCutoff, currCutoff },
      6,
      selectorsHelpers.memoizeWithKeyRetrieveCache,
    );

    return selectorsHelpers.memoizeWithKey(
      'planned-spending-calculation',
      () => {
        const retArr: TransactionRecord[] = [];
        let sum = 0;
        const localTime = DateTime.local();
        let firstTxnDate = localTime;
        const pastDateTime = DateTime.fromSeconds(pastCutoff);
        const currDateTime = DateTime.fromSeconds(currCutoff);
        txnsByAccountId?.forEach((txns) => {
          txns?.forEach((txn) => {
            const txnDate: DateTime = transactionsUtils.dateTimeFromTxn(txn);
            if (firstTxnDate > txnDate) {
              firstTxnDate = txnDate;
            }
            const txnCat = categories.get(txn?.coa?.id ?? '');
            const searchedCat = categories.get(coaId);
            if (
              (txn?.coa?.id === coaId || txnCat?.parentId === searchedCat?.id) &&
              txnDate > pastDateTime &&
              txnDate < currDateTime &&
              !txn.stModelId
            ) {
              retArr.push(txn);
              sum += txn.amount;
            } else if (transactionsUtils.isSplitTxn(txn)) {
              txn?.split?.items?.forEach((x) => {
                if (x?.coa?.id === coaId) {
                  retArr.push(txn);
                  sum += x.amount;
                }
              });
            }
          });
        });
        // We don't want scheduled to include anything past the 5th of the month
        const firstTxnDateCutoff = firstTxnDate.startOf('month').plus({ days: 4 }).endOf('day');
        let numMonths = 0;
        if (firstTxnDate > firstTxnDateCutoff && firstTxnDate > pastDateTime) {
          sum = 0;
          retArr.filter((x) => {
            const txnDate = transactionsUtils.dateTimeFromTxn(x);
            if (txnDate.monthLong !== firstTxnDate.monthLong) {
              sum += x.amount;
              return true;
            }
            return false;
          });
          numMonths = Math.round(localTime.startOf('month').diff(firstTxnDate.startOf('month'), 'months').months) - 1;
        } else if (firstTxnDate > pastDateTime) {
          numMonths = Math.round(localTime.startOf('month').diff(firstTxnDate.startOf('month'), 'months').months);
        } else {
          numMonths = 6;
        }
        return { txns: retArr, numMonths, sum };
      },
      cacheKey,
      6,
    );
  },
);

// ========== PLANNED SPENDING SELECTORS START HERE ============

export const getRolloverForItem = (
  items: (PlannedSpendingItem | undefined)[] | undefined,
  myStartDate: string,
  prevPlannedItem: PlannedSpendingItem | null | undefined,
  plannedItem: PlannedSpendingItem | null | undefined,
  futureFeatureFlag: boolean,
  currentDate: string,
  onlyCalculated?: boolean,
): number | null => {
  const startDate = myStartDate || (DateTime.fromISO(currentDate).startOf('month').toISODate() as string);
  const thisMonth = DateTime.fromISO(currentDate).startOf('month').toISODate() as string;
  const monthAfter = DateTime.fromISO(currentDate).startOf('month').plus({ months: 1 }).toISODate() as string;

  // If the rolloverType for the selected item is NONE, we do NOT show rollovers at ALL.
  if (plannedItem && !validRolloverStates.includes(plannedItem?.rolloverType)) {
    return 0;
  }

  // If the month is past the current month, we do NOT show rollovers unless the futureFeatureFlag is turned ON
  if (thisMonth < startDate && !futureFeatureFlag) {
    return 0;
  }

  // If the rolloverResetValue is set, we set rollovers to that amount
  if (typeof plannedItem?.resetRolloverAmount === 'number' && !onlyCalculated) {
    return plannedItem?.resetRolloverAmount as number;
  }

  // This is the cumulative rollover value of the plannedSpendingItem from the latest month before the selected month.
  let rolloverValue: number | null;
  if (prevPlannedItem && !validRolloverStates.includes(prevPlannedItem?.rolloverType)) {
    rolloverValue = onlyCalculated ? null : 0;
  } else {
    rolloverValue = prevPlannedItem?.cumulativeRolloverAmount || (onlyCalculated ? null : 0);
  }

  // QCS calculates the rolloverAmounts up to todaysDate, and todaysDates rolloverAmount is used in NEXT MONTHS plannedSpendingCard
  // Therefore, if the startDate is less than or EQUAL TO the month AFTER todaysDates' month, we will just use the available rolloverValue!
  // Weird huh?
  if (monthAfter >= startDate || !futureFeatureFlag) {
    return rolloverValue;
  } else {
    items?.forEach((item) => {
      if (typeof rolloverValue !== 'number') {
        rolloverValue = 0; // This is if the last item ISN'T null and we need to keep counting after!
      }
      if (item) {
        const target = item.overwrittenTargetAmount ?? (item.targetAmount || 0);
        const leftToSpend = target + (item.calculatedSpentAmount || 0);

        // We reset the rolloverAmount to nullify old values then add up the latest amounts
        if (typeof item?.resetRolloverAmount === 'number') {
          rolloverValue = item?.resetRolloverAmount as number;
          rolloverValue += leftToSpend;
        } else if (typeof item?.cumulativeRolloverAmount === 'number') {
          rolloverValue = item?.cumulativeRolloverAmount as number;
        } else if (
          // We cancelled the rollovers for this filterId at a later date,
          // but since the startDate item has a rollover, it means we re-enabled it later
          !validRolloverStates.includes(item?.rolloverType)
        ) {
          rolloverValue = 0;
        } else {
          // Finally, if all the checks pass, we sum up the value! Hooray!
          rolloverValue += leftToSpend;
        }
      } else {
        rolloverValue = onlyCalculated ? null : 0; // This is so we can end on null if the last item doesn't exist!
      }
    });
  }
  // We are intentionally returning null when there is no previous months' data.
  return rolloverValue;
};

// Checks if all planned spending items for a date are created on the QCS side.
// If we check this first, we can safely be certain we are only sending QCS real PSIs and not
// optimistically updated ones that do not exist to them yet.
export const doPlannedSpendingItemsForDateAllHaveIds = createSelector(
  [getResourcesById, (state, date: string | undefined) => date || dateSelectors.getStartOfMonthDate(state)],
  (intervals, date) => {
    const freeToSpend = intervals.find((interval) => interval.date === date);
    return freeToSpend?.plannedSpendingItems?.every((item) => !!item?.id) || false;
  },
);

export const getPlannedSpendingItemsForDate = createSelector(
  [getResourcesById, (state, date: string | undefined) => date || dateSelectors.getStartOfMonthDate(state)],
  (intervals, date) => {
    const freeToSpend = intervals.find((interval) => interval.date === date);
    return (
      freeToSpend?.plannedSpendingItems?.sortBy(
        (item) => item.name,
        (a, b) => a?.localeCompare(b),
      ) || emptyPlannedList
    );
  },
);

export const getPlannedItemByDateAndId = createSelector(
  [getPlannedSpendingItemsForDate, (_state, _startDate: string, plannedId: string) => plannedId],
  (plannedItems, id) => plannedItems.find((item) => item.id === id) || null,
);

export const getPlannedItemByDateAndFilterId = createSelector(
  [
    (state, startDate: string) => getPlannedSpendingItemsForDate(state, startDate),
    (_state, _startDate: string, filterId: string) => filterId,
  ],
  (plannedItems, id) => plannedItems.find((item) => item.filterId === id) || null,
);

// Get all intervals during and after the current date. We do not need to worry about any intervals before the current date because those are calculated
// by QCS and all we need to do in that case is call (getPreviousMonthsPlannedItemByDateAndFilterId)
export const getFutureFreeToSpendIntervalsIncludingToday = createSelector(
  getResourcesById,
  dateSelectors.getCurrentDateString,
  (intervals, currentDate) => {
    const date = DateTime.fromISO(currentDate).startOf('month').toISODate() as string;
    return intervals.filter((interval) => interval?.date && interval.date >= date).sortBy((interval) => interval.date);
  },
);

// Get either today's date or the interval closest to today's date (after today's date) This is how we determine which
// planned spending item will be affected by edits made to rollovers! (We only allow the user to edit today's dates item, and if it does
// not exist, then we default to the next available future item)
export const getEarliestPlannedSpendingItemNotInPastByFilterId = createSelector(
  [getFutureFreeToSpendIntervalsIncludingToday, (_state, filterId: string) => filterId],
  (intervals, filterId) => {
    let selectedItem: null | PlannedSpendingItem | undefined = null;
    let date: null | string = null;
    let f2sObject: null | FreeToSpend = null;

    // Search the months for the planned spending item with the same filterId.
    for (const f2sMonth of intervals.values()) {
      const newItem = f2sMonth?.plannedSpendingItems?.find((item) => item.filterId === filterId);
      if (newItem) {
        f2sObject = f2sMonth;
        date = f2sMonth.date;
        selectedItem = newItem;
        break; // Stop iteration once the first matching item is found.
      }
    }

    return {
      f2sObject,
      selectedItem,
      date,
    };
  },
);

// Search through the future intervals to grab the correct plannedSpending items by filterId. They should be after todaysDate, but BEFORE the STARTDATE.
export const getPlannedSpendingItemsBeyondDateByFilterId = createSelector(
  [
    getFutureFreeToSpendIntervalsIncludingToday,
    (_state, _startDate: string, filterId: string) => filterId,
    (_state, startDate: string) => startDate,
    dateSelectors.getCurrentDateString,
  ],
  (intervals, filterId, myStartDate, currentDate) => {
    const startDate = myStartDate || DateTime.fromISO(currentDate).startOf('month').toISODate();
    const plannedItemList: (PlannedSpendingItem | undefined)[] = [];

    const intervalsToSum = intervals
      .filter((month) => month?.date && startDate && startDate > month.date)
      .sortBy((interval) => interval.date);

    // Search the months for the planned spending item with the same filterId.
    intervalsToSum.forEach((f2sMonth) => {
      const newItem = f2sMonth?.plannedSpendingItems?.find((plannedItem) => {
        return plannedItem.filterId === filterId;
      });
      plannedItemList.push(newItem);
    });

    return plannedItemList;
  },
);

// Search through the future intervals to grab the correct plannedSpending items by filterId. They should be after todaysDate, but BEFORE the STARTDATE.
export const getPlannedSpendingItemListBeyondDateByFilterId = createSelector(
  [
    getFutureFreeToSpendIntervalsIncludingToday,
    getPlannedSpendingItemsForDate,
    (_state, startDate: string) => startDate,
    dateSelectors.getCurrentDateString,
  ],
  (intervals, plannedItemsList, myStartDate, currentDate) => {
    const filterIdList = plannedItemsList.map((item) => item?.filterId);
    return filterIdList?.map((filterId: string) => {
      const startDate = myStartDate || DateTime.fromISO(currentDate).startOf('month').toISODate() || '';
      const plannedItemList: (PlannedSpendingItem | undefined)[] = [];

      const intervalsToSum = intervals.filter((month) => startDate > month.date).sortBy((interval) => interval.date);

      // Search the months for the planned spending item with the same filterId.
      intervalsToSum.forEach((f2sMonth) => {
        const newItem = f2sMonth?.plannedSpendingItems?.find((plannedItem) => {
          return plannedItem.filterId === filterId;
        });
        plannedItemList.push(newItem);
      });

      return plannedItemList;
    });
  },
);

// This uses QCS calculations for the past and present, AND client calculations for the future!
export const getPlannedSpendingItemRolloverInfo = createSelector(
  [
    (state, _plannedId: string, filterId: string, startDate: string) =>
      getPlannedSpendingItemsBeyondDateByFilterId(state, startDate, filterId), // Get all items to sum AFTER today's date
    (_state, _plannedId: string, _filterId: string, startDate: string) => startDate, // Get the selected date
    (state, _plannedId, filterId, startDate) =>
      getPlannedItemByDateAndFilterId(
        state,
        DateTime.fromISO(startDate).startOf('month').minus({ months: 1 }).toISODate() ?? '',
        filterId,
      ), // get the previous months item
    (state, plannedId: string, _filterId: string, startDate: string) =>
      getPlannedItemByDateAndId(state, startDate, plannedId), // get the item for the SELECTED date
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanRollForward'), // Do we calculate future dates rollovers?
    dateSelectors.getCurrentDateString,
    (_state, _plannedId: string, _filterId: string, _startDate: string, onlyCalculated?: boolean) => onlyCalculated,
  ], // We ignore the reset value and ONLY get the calculated value!!
  (items, myStartDate, prevPlannedItem, plannedItem, futureFeatureFlag, currentDate, onlyCalculated) =>
    getRolloverForItem(
      items,
      myStartDate,
      prevPlannedItem,
      plannedItem,
      futureFeatureFlag,
      currentDate,
      onlyCalculated,
    ),
);

// This uses QCS calculations for the past and present, AND client calculations for the future!
export const getPlannedSpendingItemListRolloverInfo = createSelector(
  [
    getPlannedSpendingItemListBeyondDateByFilterId, // Get all items to sum AFTER the today's date // returns and array of immutableMaps
    (_state, startDate: string) => startDate, // Get the selected date
    (state, startDate: string) =>
      getPlannedSpendingItemsForDate(
        state,
        DateTime.fromISO(startDate).startOf('month').minus({ months: 1 }).toISODate() as string,
      ), // get the previous months items
    getPlannedSpendingItemsForDate, // get the items for the SELECTED date
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanRollForward'), // Do we calculate future dates rollovers?
    dateSelectors.getCurrentDateString,
  ],
  (itemList, myStartDate, prevPlannedItemList, plannedItemList, futureFeatureFlag: boolean, currentDate) =>
    plannedItemList.map((plannedItem) => {
      const prevPlannedItem = prevPlannedItemList.find((prevItem) => prevItem?.filterId === plannedItem?.filterId);
      const items = itemList?.find((myItem) => myItem?.[0]?.filterId === plannedItem?.filterId);
      return [
        plannedItem?.id,
        getRolloverForItem(items, myStartDate, prevPlannedItem, plannedItem, futureFeatureFlag, currentDate, false),
      ];
    }),
);

export const getCalculatedOnlyPlannedSpendingItemRolloverInfo = createSelector(
  (state, plannedId: string, filterId: string, startDate: string) =>
    getPlannedSpendingItemRolloverInfo(state, plannedId, filterId, startDate, true),
  (result) => result,
);

export const getPlannedItemCategoryByFilterId = createSelector(
  [(state, filterId: string) => filtersSelectors.getFilter(state, filterId), categoriesSelectors.getCategoriesById],
  (filter, categoriesById) =>
    filter?.filterItems?.[0]?.type === 'CHARTOFACCOUNT'
      ? categoriesById?.get(filter?.filterItems[0].chartOfAccounts[0]?.id)
      : null,
);

export const getPlannedSpendingItemsByF2SId = createSelector(
  getFreeToSpendById,
  (freeToSpend) =>
    freeToSpend?.plannedSpendingItems?.sortBy(
      (item) => item.name,
      (a, b) => a?.localeCompare(b),
    ) || emptyPlannedList,
);

export const getTotalTargetValueForPlannedSpending = createSelector(getPlannedSpendingItemsForDate, (plannedItems) => {
  let total = 0;
  plannedItems?.forEach((item) => {
    if (item.overwrittenTargetAmount === 0 || item.overwrittenTargetAmount) {
      total += item.overwrittenTargetAmount;
    } else {
      total += item.targetAmount;
    }
  });
  return Number(total.toFixed(2));
});

export const getCalculatedPlannedSpending = (state: State, freeToSpendId: string) => {
  const spendingPlan = getFreeToSpendById(state, freeToSpendId);
  return spendingPlan ? getPlannedSpendingAmount(state, spendingPlan) : 0;
};

export const getPlannedCoaIds = createSelector(
  getPlannedSpendingItemsForDate,
  filtersSelectors.getFilters,
  (plannedItems, filters) =>
    plannedItems
      ?.flatMap((item) => {
        const firstFilterItem = filters?.get(item.filterId)?.filterItems?.[0];
        if (!firstFilterItem) return [];
        switch (firstFilterItem.type) {
          case 'CHARTOFACCOUNT':
            return firstFilterItem.chartOfAccounts?.map((coa) => coa.id);
          default:
            return [];
        }
      })
      .toSet() || emptyStringSet,
);

const getCategories = (
  plannedItems: PlannedSpendingItems | undefined,
  filters: any,
  categories: any,
  includeUncategorized: boolean,
) => {
  const catMap: [string, any][] | any = [[]];
  let catFilter;
  // Create a map from category id to name and parent id to quickly check if parent id already exists in planned items
  plannedItems?.forEach((item) => {
    // get filter of planned item
    catFilter = filters.get(item.filterId);
    // get the cat id from filter
    const id = categoriesSelectors.getParentFromCoaListFunction(catFilter, categories);
    // get the cat
    const cat = categories.get(id);
    // loop through filter items
    catFilter?.filterItems[0]?.chartOfAccounts?.map((x: any) => {
      const catX = categories.get(x.id);
      if (item.name && catX) {
        catMap.push([x.id, { name: item.name, parentId: catX?.parentId, plannedId: item.id }]);
      } else if (cat && catX) {
        const { name } = cat;
        const { parentId } = catX;
        catMap.push([x.id, { name, parentId, plannedId: item.id }]);
      } else if (includeUncategorized && id === '0') {
        catMap.push([x.id, { name: 'Uncategorized', parentId: '0', plannedId: '0' }]);
      }
      return x;
    });
  });
  return ImmutableMap(catMap);
};

export const getPlannedSpendingCoas = createSelector(
  [
    getPlannedSpendingItemsForDate,
    filtersSelectors.getFilters,
    categoriesSelectors.getCategoriesById,
    (_state, _startDate: string, includeUncategorized: boolean) => includeUncategorized,
  ],
  (plannedItems, filters, categories, includeUncategorized) =>
    getCategories(plannedItems, filters, categories, includeUncategorized),
);

const getExistingFutureResources = createSelector(
  [getResourcesById, (_state, date: string) => date],
  (resources, date) =>
    resources?.filter((resource) => resource?.date && resource.date > date && !resource.isDeleted) || ImmutableMap(),
);

export const getFuturePlannedSpendingCoas = createSelector(
  [
    getExistingFutureResources,
    filtersSelectors.getFilters,
    categoriesSelectors.getCategoriesById,
    (_state, _startDate: unknown, includeUncategorized: boolean) => includeUncategorized,
  ],
  (futureSpendingPlans, filters, categories, includeUncategorized) =>
    futureSpendingPlans.flatMap((spendingPlan) =>
      getCategories(spendingPlan.plannedSpendingItems, filters, categories, includeUncategorized),
    ),
);

export const earliestIntervalDate = createSelector(getResourcesById, (intervals) => {
  const minInterval =
    intervals &&
    intervals
      .toList() // @ts-expect-error TS2362, TS2363 // TS doesn't like subtracting dates
      .min((interval1, interval2) => DateTime.fromISO(interval1.date) - DateTime.fromISO(interval2.date));
  return minInterval && DateTime.fromISO(minInterval.date as string);
});

// helper to provide stable IDs list for plannedSpending
const getIdsFromPlannedSpending = _memoize((plannedSpending: PlannedSpendingItem) =>
  plannedSpending?.txnIds?.length || plannedSpending?.excludedTxnIds?.length
    ? ImmutableList<string>(
        plannedSpending?.txnIds?.length
          ? plannedSpending.txnIds.concat(plannedSpending?.excludedTxnIds ?? []) // Make sure we include excluded txns.
          : plannedSpending?.excludedTxnIds ?? [],
      )
    : emptyStringList,
);

// doesn't need to be a selector itself as it is a wrapper for a memoized selector
const getQCSTransactionsForPlannedSpendingItem = (state: State, plannedSpending: PlannedSpendingItem) =>
  !featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc')
    ? transactionsSelectors.getSortedTransactionsByIds(state, getIdsFromPlannedSpending(plannedSpending))
    : null;

// TODO: deprecate once splits enabled
export const getTransactionsForPlannedSpendingItem = createWeakMapSelector.withTypes<State>()(
  [
    (_state, props: { date: string }) => props.date,
    (_state, props: { plannedSpending: PlannedSpendingItem }) => props.plannedSpending,
    filtersSelectors.getFilters,
    (state, props: { date: string }) => getTransactionsForMonth(state, props.date),
    (state, props: { plannedSpending: PlannedSpendingItem }) =>
      getQCSTransactionsForPlannedSpendingItem(state, props.plannedSpending),
  ],
  (date, plannedSpending, filters, transactions, qcsTxns) => {
    if (qcsTxns) {
      return qcsTxns;
    }
    const filterObject = filtersUtils.convertQCSFilterToFilterObject(filters.get(plannedSpending?.filterId));
    const txnList = transactions ?? emptyTxnList;
    return transactionsUtils.filterTransactionsByFilterObject(
      txnList,
      filterObject.merge({
        dateStart: date,
        dateEnd: DateTime.fromISO(date).endOf('month').toISODate(),
        advanced: filtersTypes.AdvancedFilterObject({ isBillOrSubscription: false }),
      }),
    );
  },
);

// has splitItem support
export const getTransactionItemsForPlannedSpendingItem = createWeakMapSelector.withTypes<State>()(
  [
    (state, props: { plannedSpending: PlannedSpendingItem }) =>
      filtersSelectors.getFilters(state)?.get(props.plannedSpending?.filterId),
    (state, props: { date: string }) => getTransactionsForMonth(state, props.date),
  ],
  (filter, transactions) => {
    const itemsArray: TransactionItem[] = [];

    if (filter) {
      // @ts-expect-error ts(2339) // thinks it can be not a COA type (but PSIs are always COA type)
      const coaIdArray = filter?.filterItems?.[0]?.chartOfAccounts?.map((coa) => coa.id);

      transactions.forEach((txn) => {
        if (!txn.isBill && !txn.isSubscription) {
          if (transactionsUtils.isSplitTxn(txn)) {
            txn.split?.items?.forEach((splitItem) => {
              if (splitItem.coa?.id && coaIdArray.includes(splitItem.coa?.id)) {
                itemsArray.push(
                  mkTransactionItem({
                    transaction: txn,
                    splitItem: splitItem as RecordOf<SplitItemProps>,
                    item: {
                      ...txn.toObject(),
                      ...(Record.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
                    },
                  }),
                );
              }
            });
          } else if (txn.coa?.id && coaIdArray?.includes(txn.coa?.id)) {
            itemsArray.push(
              mkTransactionItem({
                transaction: txn,
                item: txn.toObject(),
              }),
            );
          }
        }
      });
    }

    return ImmutableList<TransactionItem>(itemsArray);
  },
);

export const getCalculatedValueForPlannedSpendingItem = createSelector(
  // TODO: replace with TransactionItemsForPlannedSpending once splits adopted
  [getTransactionsForPlannedSpendingItem, (_state, props) => props.plannedSpending, filtersSelectors.getFilters],
  (transactions, plannedSpending, filters) => {
    const coaIdMap = filters
      .get(plannedSpending?.filterId) // @ts-expect-error TS2339
      ?.filterItems?.[0]?.chartOfAccounts?.map((coa) => coa.id);
    return getTxnSumForCoa(Array.isArray(transactions) ? ImmutableList(transactions) : transactions, Set(coaIdMap));
  },
);

export const getCalculatedPlannedSpendingObject = createSelector(
  [
    getPlannedSpendingItemsForDate,
    (_state, date: string) => date,
    filtersSelectors.getFilters,
    getTransactionsForMonth,
    transactionsSelectors.getDateOnAfter,
    (state) => featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc'),
  ],
  (plannedItemsForDate, date, filters, transactions, dateOnAfter, spendingPlanClientCalc) => {
    const retObj: any = {};
    plannedItemsForDate?.forEach((item) => {
      retObj[item.id] = {
        rolloverType: item?.rolloverType,
        calculatedIncluded:
          spendingPlanClientCalc && (!dateOnAfter || dateOnAfter < date)
            ? getValueFromFilterAndTransactions({ filters, transactions, filterId: item.filterId, date })
            : item.calculatedSpentAmount,
        overwrittenTargetAmount: item.overwrittenTargetAmount,
        targetAmount: item.targetAmount,
      };
    });
    return retObj;
  },
  {
    memoize: lruMemoize,
    memoizeOptions: {
      resultEqualityCheck: (a, b) => {
        if (a === b) {
          return true;
        }
        if (a && b) {
          if (typeof a === 'string' && typeof b === 'string') {
            return a === b;
          }
          if (typeof a === 'object' && typeof b === 'object') {
            return Object.keys(a).length === Object.keys(b).length && Object.keys(a).every((key) => a[key] === b[key]);
          }
        }
        return false;
      },
      maxSize: 10,
    },
  },
);

export const getCalculatedValueForPlannedSpending = createSelector(
  [
    (state, date: string) => getPlannedSpendingItemsForDate(state, date),
    (state, date: string) => getCalculatedPlannedSpendingObject(state, date),
  ],
  (plannedItems, plannedItemObj) =>
    plannedItems?.reduce(
      (accumulator, planned) => accumulator + plannedItemObj?.[planned?.id]?.calculatedIncluded,
      0,
    ) || 0,
);

export const getPlannedSpendingAmount = (state: State, freeToSpend: FreeToSpend) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    return freeToSpend?.calculatedPlannedSpendingAmount;
  }
  return getPlannedSpendingAmountSelector(state, freeToSpend);
};

export const getPlannedSpendingAmountSelector = createSelector(
  [
    (state, freeToSpend: FreeToSpend) => getPlannedSpendingItemsForDate(state, freeToSpend.date),
    (state, freeToSpend: FreeToSpend) => getCalculatedPlannedSpendingObject(state, freeToSpend.date),
  ],
  (plannedItems, plannedItemObj) => {
    return (
      plannedItems?.reduce((accumulator, planned) => {
        const item = plannedItemObj[planned.id];

        const targetAmount = item.overwrittenTargetAmount ?? item.targetAmount;

        if (validRolloverStates.includes(item?.rolloverType)) {
          return accumulator + targetAmount * -1;
        }

        // need to account for positive spending, so cannot do math.abs()
        if (targetAmount > item.calculatedIncluded * -1) {
          return accumulator + targetAmount * -1;
        }

        return accumulator + item.calculatedIncluded;
      }, 0) || 0
    );
  },
);

export const getPlannedSpendingAmountForDate = (state: State, date: string, id: string) =>
  getCalculatedPlannedSpendingObject(state, date)?.[id]?.calculatedIncluded || 0;

export const isPlannedOverSpent = createSelector(
  getCalculatedPlannedSpendingObject,
  getPlannedSpendingItemsForDate,
  (plannedObj, plannedItems) =>
    plannedItems?.some(
      (item) =>
        // Multiplying by -1 because the value is already negative, and we must compare it to positive values.
        plannedObj[item.id]?.calculatedIncluded * -1 > (item.overwrittenTargetAmount ?? item.targetAmount) &&
        !validRolloverStates.includes(item?.rolloverType),
    ),
);

export const isPlannedOverSpentIncludingRollovers = createSelector(
  [
    getCalculatedPlannedSpendingObject,
    getPlannedSpendingItemsForDate,
    (state, startDate: string) => getPlannedSpendingItemListRolloverInfo(state, startDate),
  ],
  (plannedObj, plannedItems, rolloverList: any) =>
    plannedItems?.some((item) => {
      const rollover: number =
        Number(rolloverList?.find(([key, _rollover]: [key: string, _rollover: any]) => item?.id === key)[1]) || 0;
      // Multiplying by -1 because the value is already negative, and we must compare it to positive values.
      let spentAmount = plannedObj[item.id]?.calculatedIncluded * -1;
      let targetAmount = item.overwrittenTargetAmount ?? item.targetAmount;

      if (rollover < 0) {
        spentAmount -= rollover;
      } else {
        targetAmount += rollover;
      }
      return spentAmount > targetAmount;
    }),
);

export const getPlannedOverSpentQty = createSelector(
  getCalculatedPlannedSpendingObject,
  getPlannedSpendingItemsForDate,
  (plannedObj, plannedItems) =>
    plannedItems?.filter(
      (item) =>
        // Multiplying by -1 because the value is already negative, and we must compare it to positive values
        plannedObj[item.id]?.calculatedIncluded * -1 > (item.overwrittenTargetAmount ?? item.targetAmount) &&
        !validRolloverStates.includes(item?.rolloverType),
    )?.size,
);

export const getAverageSpendingForCategory = createSelector(getTransactionsForCoa, (transactions) => {
  const cacheKey = selectorsHelpers.getOrCreateStringFromObject(
    'planned-average',
    { transactions },
    6,
    selectorsHelpers.memoizeWithKeyRetrieveCache,
  );

  return selectorsHelpers.memoizeWithKey(
    'planned-average',
    () => {
      if (!transactions) return { averageSpending: 0, numMonths: 0 };
      const { sum, numMonths } = transactions;
      return { averageSpending: numMonths <= 0 ? 0 : sum / numMonths, numMonths };
    },
    cacheKey,
    6,
  );
});

// ========== PLANNED SPENDING SELECTORS END HERE ============

// ========== SPENDING PLAN SELECTORS START HERE =============

const getIncludedAndExcludedSpentIds = createSelector(getFreeToSpendById, (freeToSpend) =>
  freeToSpend?.spentTxnIds?.length || freeToSpend?.excludedSpentTxnIds?.length
    ? ImmutableList(
        freeToSpend?.spentTxnIds?.length
          ? freeToSpend?.spentTxnIds.concat(freeToSpend?.excludedSpentTxnIds ?? [])
          : freeToSpend?.excludedSpentTxnIds ?? [],
      )
    : emptyStringList,
);

// TODO: update to support txnItems later
const getQCSOtherSpendingTxns = createSelector(
  [
    (state, id: string) =>
      transactionsSelectors.getTransactionsByIds(
        state,
        getIncludedAndExcludedSpentIds(state, id),
        'ftsTransactionsSpent',
      ),
  ],
  (qcsTxns) => {
    if (qcsTxns) {
      // if ignored, display in list but not in chart
      const chartTxns = qcsTxns?.filter((txn) => !txn.isExcludedFromF2S);
      return { listTxns: qcsTxns ?? emptyTxnList, chartTxns: chartTxns ?? emptyTxnList };
    }
    return { listTxns: emptyTxnList, chartTxns: emptyTxnList };
  },
);

// TODO: deprecate once splits adopted
export const getOtherSpendingTxns = (state: State, id: string) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    return getQCSOtherSpendingTxns(state, id);
  }
  const date =
    getFreeToSpendById(state, id)?.date ?? (dateSelectors.getCurrentDate(state).startOf('month').toISODate() as string);
  return getOtherSpendingTxnsSelector(state, date);
};

// TODO: deprecate once splits adopted
export const getOtherSpendingTxnsSelector = createSelector(
  [
    (_state, date: string) => date,
    getTransactionsForMonth,
    (state, date: string) =>
      getPlannedCoaIds(
        // returns immutable set
        state,
        date ?? (dateSelectors.getCurrentDate(state).startOf('month').toISODate() as string),
      ),
    categoriesSelectors.getIncomeCOAIds,
    (state) =>
      accountsSelectors.getLoadPending(state) ||
      categoriesSelectors.getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state) ||
      getLoadPending(state),
  ],
  (_date, transactions, plannedCats, incomeCats, loadPending) => {
    if (loadPending) {
      return { listTxns: emptyTxnList, chartTxns: emptyTxnList };
    }
    const chart: TransactionRecord[] = [];
    const categoriesToExclude = plannedCats.concat(incomeCats);
    const listTxns = transactions.filter((txn) => {
      if (!txn.isBill && !txn.isSubscription) {
        if (transactionsUtils.isSplitTxn(txn)) {
          if (
            !txn.split?.items?.some((item) => !categoriesToExclude.has(item.coa?.id ?? '')) ||
            !txn.split?.items?.some((item) => !transactionsUtils.isTransferOrCreditCardPaymentTxn(item))
          ) {
            // ignore split if no items are untracked COAs
            // ignore split if all items are tranfers or credit card payments
            return false;
          }
        } else if (categoriesToExclude.has(txn.coa?.id ?? '')) {
          // drop already tracked categories
          return false;
        } else if (transactionsUtils.isTransferOrCreditCardPaymentTxn(txn)) {
          // drop transfers and credit card payments
          return false;
        }
        if (!txn.isExcludedFromF2S) {
          chart.push(txn);
        }
        return true;
      }
      return false;
    });
    return { listTxns, chartTxns: ImmutableList(chart) };
  },
  {
    memoize: lruMemoize,
    memoizeOptions: {
      resultEqualityCheck: (a, b) => {
        if (a === b) {
          return true;
        }
        if (a && b) {
          if (
            a.listTxns?.hashCode?.() === b.listTxns?.hashCode?.() &&
            a.chartTxns?.hashCode?.() === b.chartTxns?.hashCode?.()
          ) {
            return (
              a.listTxns.size === b.listTxns.size &&
              a.chartTxns.size === b.chartTxns.size &&
              a.listTxns.equals(b.listTxns) &&
              a.chartTxns.equals(b.chartTxns)
            );
          }
        }
        return false;
      },
      maxSize: 10,
    },
  },
);

export const getOtherSpendingTxnsWithSplitsSelector = createSelector(
  [
    getTransactionsForMonth,
    (state, date: string) =>
      getPlannedCoaIds(
        // returns immutable set
        state,
        date ?? (dateSelectors.getCurrentDate(state).startOf('month').toISODate() as string),
      ),
    categoriesSelectors.getIncomeCOAIds,
    (state) =>
      accountsSelectors.getLoadPending(state) ||
      categoriesSelectors.getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state) ||
      getLoadPending(state),
  ],
  (transactions, plannedCats, incomeCats, loadPending) => {
    if (loadPending) {
      return { listTxns: emptyItemsList, chartTxns: emptyTxnList };
    }
    const categoriesToExclude = plannedCats.concat(incomeCats);
    const hasCoaToDisplay = (item: TransactionProps | SplitItemProps) =>
      !categoriesToExclude.has(item?.coa?.id ?? '') && !transactionsUtils.isTransferOrCreditCardPaymentTxn(item);

    const chart: TransactionRecord[] = [];
    const itemArray: TransactionItem[] = [];
    transactions.forEach((txn) => {
      if (!txn.isBill && !txn.isSubscription) {
        // split here
        if (transactionsUtils.isSplitTxn(txn)) {
          let shouldPush = false;
          txn.split?.items?.forEach((splitItem) => {
            if (hasCoaToDisplay(splitItem)) {
              shouldPush = true;
              itemArray.push(
                mkTransactionItem({
                  transaction: txn,
                  splitItem: splitItem as RecordOf<SplitItemProps>,
                  item: {
                    ...txn.toObject(),
                    ...(Record.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
                  },
                }),
              );
            }
          });
          if (shouldPush && !txn.isExcludedFromF2S) {
            chart.push(txn);
          }

          // non-split case
        } else if (hasCoaToDisplay(txn)) {
          itemArray.push(
            mkTransactionItem({
              transaction: txn,
              item: txn.toObject(),
            }),
          );
          if (!txn.isExcludedFromF2S) {
            chart.push(txn);
          }
        }
      }
    });

    return {
      listTxns: ImmutableList<TransactionItem>(itemArray),
      chartTxns: ImmutableList<TransactionRecord>(chart),
    };
  },
  {
    memoize: lruMemoize,
    memoizeOptions: {
      resultEqualityCheck: (a, b) => {
        if (a === b) {
          return true;
        }
        if (a && b) {
          if (
            a.listTxns?.hashCode?.() === b.listTxns?.hashCode?.() &&
            a.chartTxns?.hashCode?.() === b.chartTxns?.hashCode?.()
          ) {
            return (
              a.listTxns.size === b.listTxns.size &&
              a.chartTxns.size === b.chartTxns.size &&
              a.listTxns.equals(b.listTxns) &&
              a.chartTxns.equals(b.chartTxns)
            );
          }
        }
        return false;
      },
      maxSize: 10,
    },
  },
);

export const getOtherSpendingTxnsWithSplits = (state: State, id: string) => {
  const date =
    getFreeToSpendById(state, id)?.date ?? (dateSelectors.getCurrentDate(state).startOf('month').toISODate() as string);
  return getOtherSpendingTxnsWithSplitsSelector(state, date);
};

export const getSpendingPlanGoalsBreakdownData = createSelector(
  [
    goalsSelectors.getGoalsById,
    (state, freeToSpend: FreeToSpend) => getTransactionsForMonthWithGoals(state, freeToSpend.date),
    (state, freeToSpend: FreeToSpend) => freeToSpend.date,
    (state) =>
      goalsSelectors.getLoadPending(state) ||
      accountsSelectors.getLoadPending(state) ||
      getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state),
  ],
  (goals, transactions, date, loadPending) => {
    if (loadPending) {
      return { includeData: emptyGoalsList, excludeData: emptyGoalsList, excludedAmount: 0, includedAmount: 0 };
    }

    let excludedAmount = 0;
    let includedAmount = 0;
    const includeDataArray: Goal[] = [];
    const excludedDataArray: Goal[] = [];

    // Would it make sense to do it elsewhere?
    const txnsByGoalId = transactions.groupBy((transaction) => transaction.accountId);

    goals?.forEach((goal: Goal) => {
      const goalTxns = txnsByGoalId?.get(goal.accountId);
      const hasTxns = Boolean(goalTxns?.first()); // size is not in Collection
      const monthlyContribution = hasTxns ? goalsUtils.getMonthlyContribution(goalTxns) : 0; // sum of actual contributions

      const goalStarted = date >= goal.startOn;
      if ((!goalStarted || goal.completedOn) && !hasTxns) {
        // only show contributions for goals that have expected contributions (so new goals don't retroactively change numbers)
        return;
      }

      let goalAmount = 0;
      if (!goalStarted) {
        // don't apply projected contributions in the past
        goalAmount = monthlyContribution; // only displays actual contributions
      } else {
        // Choose larger between the expected contribution amount or the current contribution amount
        goalAmount =
          !goal.contributionAmount || monthlyContribution > goal.contributionAmount
            ? monthlyContribution
            : goal.contributionAmount;
      }

      if (goal.isTakenFromF2S) {
        includeDataArray.push(goal);
        includedAmount += goalAmount;
      } else {
        excludedDataArray.push(goal);
        excludedAmount += goalAmount;
      }
    });

    return {
      includeData: ImmutableList(includeDataArray).sort((goal1, goal2) => goal1.name?.localeCompare(goal2.name)),
      excludeData: ImmutableList(excludedDataArray).sort((goal1, goal2) => goal1.name?.localeCompare(goal2.name)),
      excludedAmount: excludedAmount * -1,
      includedAmount: includedAmount * -1,
    };
  },
);

const getQCSGoalContributionForCurrentMonth = (goals: ImmutableList<Goal>) =>
  goals.reduce((acc: number, goal: Goal) => {
    const { contributedThisMonth = 0, contributionAmount } = goal;
    // Choose larger between the expected contribution amount or the current contribution amount
    const amount =
      !contributionAmount || contributedThisMonth > contributionAmount ? contributedThisMonth : contributionAmount;
    return acc + amount;
  }, 0) || 0;

const getQCSSpendingPlanGoalsBreakdownData = createSelector(
  [
    (state, freeToSpend: FreeToSpend) => goalsSelectors.getGoalsByIds(state, freeToSpend.goalsIds ?? []),
    (state, freeToSpend: FreeToSpend) => goalsSelectors.getGoalsByIds(state, freeToSpend.excludedGoalsIds ?? []),
  ],
  (includeData, excludeData) => {
    const excludedAmount = getQCSGoalContributionForCurrentMonth(excludeData);
    const includedAmount = getQCSGoalContributionForCurrentMonth(includeData);

    return { includeData, excludeData, excludedAmount: excludedAmount * -1, includedAmount: includedAmount * -1 };
  },
);

let serverBreakdownCalcNumber = 0;
let breakdownCalcNumber = 0;
const getQCSSpendingPlanBreakdownData = createSelector(
  [
    categoriesSelectors.getIncomeCOAIds,
    getQCSSpendingPlanGoalsBreakdownData,
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend.incomeTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend.excludedIncomeTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend.billsTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend.excludedBillsTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend.transferTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend.excludedTransferTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend.subscriptionsTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend.excludedSubscriptionsTxnIds),
  ],
  (
    incomeCoas,
    goalsData,
    includedIncomeData,
    excludedIncomeData,
    includedBillsData,
    excludedBillsData,
    includedTransfersData,
    excludedTransfersData,
    includedSubscriptionsData,
    excludedSubscriptionsData,
  ) => {
    serverBreakdownCalcNumber += 1;
    return {
      income: {
        includeData: includedIncomeData,
        excludeData: excludedIncomeData,
        excludedAmount: getTxnSumForCoa(excludedIncomeData, incomeCoas),
        includedAmount: getTxnSumForCoa(includedIncomeData, incomeCoas),
      },
      bills: {
        includeData: includedBillsData,
        excludeData: excludedBillsData,
        excludedAmount: getTxnSum(excludedBillsData),
        includedAmount: getTxnSum(includedBillsData),
      },
      transfers: {
        includeData: includedTransfersData,
        excludeData: excludedTransfersData,
        excludedAmount: getTxnSum(excludedTransfersData),
        includedAmount: getTxnSum(includedTransfersData),
      },
      subscriptions: {
        includeData: includedSubscriptionsData,
        excludeData: excludedSubscriptionsData,
        excludedAmount: getTxnSum(excludedSubscriptionsData),
        includedAmount: getTxnSum(includedSubscriptionsData),
      },
      goals: goalsData,
    };
  },
);

// TODO: deprecate after splits fully adopted
const getSpendingPlanBreakdownDataSelector = createSelector(
  [
    (_state, freeToSpend: FreeToSpend) => freeToSpend,
    (state, freeToSpend: FreeToSpend) => getTransactionsForMonth(state, freeToSpend.date),
    categoriesSelectors.getIncomeCOAIds,
    categoriesSelectors.getExpenseCOAIds,
    transactionsSelectors.getDateOnAfter,
    getSpendingPlanGoalsBreakdownData,
    (state) =>
      accountsSelectors.getLoadPending(state) ||
      categoriesSelectors.getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state) ||
      getLoadPending(state),
  ],
  (freeToSpend, transactions, incomeCoas, expenseCoas, earliestDate, goalsData, loadPending) => {
    breakdownCalcNumber += 1;
    if (loadPending) {
      return {
        income: {
          includeData: emptyTxnList,
          excludeData: emptyTxnList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedIncomeAmount ?? 0,
        },
        bills: {
          includeData: emptyTxnList,
          excludeData: emptyTxnList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedBillsAmount ?? 0,
        },
        transfers: {
          includeData: emptyTxnList,
          excludeData: emptyTxnList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedTransferAmount ?? 0,
        },
        subscriptions: {
          includeData: emptyTxnList,
          excludeData: emptyTxnList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedSubscriptionsAmount ?? 0,
        },
        goals: {
          includeData: emptyTxnList,
          excludeData: emptyTxnList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedSubscriptionsAmount ?? 0,
        },
      };
    }
    const txnsIncomeIncludedArray: TransactionRecord[] = [];
    const txnsIncomeExcludedArry: TransactionRecord[] = [];
    const txnsBillsIncludedArray: TransactionRecord[] = [];
    const txnsBillsExcludedArry: TransactionRecord[] = [];
    const txnsTransfersIncludedArray: TransactionRecord[] = [];
    const txnsTransfersExcludedArry: TransactionRecord[] = [];
    const txnsSubscriptionsIncludedArray: TransactionRecord[] = [];
    const txnsSubscriptionsExcludedArry: TransactionRecord[] = [];

    const bucketizeTxnItem = (txn: TransactionRecord, splitItem?: SplitItemProps) => {
      const dataSource = splitItem ? splitItem : txn;
      const isTransfer = transactionsUtils.isTransferOrCreditCardPaymentTxn(dataSource);
      if (isTransfer && txn.subtype !== 'GOAL_SPENT') {
        // ===== TRANSFER ==========
        if (!txn.isExcludedFromF2S) {
          if (!txnsTransfersIncludedArray.includes(txn)) {
            // won't need de-dupe logic when we move to txnItem list
            txnsTransfersIncludedArray.push(txn);
          }
        } else {
          if (!txnsTransfersExcludedArry.includes(txn)) {
            txnsTransfersExcludedArry.push(txn);
          }
        }
      } else if (incomeCoas.has(dataSource?.coa?.id as string)) {
        // ===== INCOME =====
        if (!txn.isExcludedFromF2S) {
          if (!txnsIncomeIncludedArray.includes(txn)) {
            txnsIncomeIncludedArray.push(txn);
          }
        } else {
          if (!txnsIncomeExcludedArry.includes(txn)) {
            txnsIncomeExcludedArry.push(txn);
          }
        }
      } else if (txn.isBill) {
        // ==== BILL ======
        if (!txn.isExcludedFromF2S) {
          if (!txnsBillsIncludedArray.includes(txn)) {
            txnsBillsIncludedArray.push(txn);
          }
        } else {
          if (!txnsBillsExcludedArry.includes(txn)) {
            txnsBillsExcludedArry.push(txn);
          }
        }
      } else if (txn.isSubscription) {
        // ===== SUBSCRIPTION =======
        if (!txn.isExcludedFromF2S) {
          if (!txnsSubscriptionsIncludedArray.includes(txn)) {
            txnsSubscriptionsIncludedArray.push(txn);
          }
        } else {
          if (!txnsSubscriptionsExcludedArry.includes(txn)) {
            txnsSubscriptionsExcludedArry.push(txn);
          }
        }
      }
    };

    transactions.forEach((txn) => {
      if (transactionsUtils.isSplitTxn(txn)) {
        txn.split?.items?.forEach((item) => bucketizeTxnItem(txn, item));
      } else {
        bucketizeTxnItem(txn);
      }
    });

    const txnsIncomeIncluded = ImmutableList(txnsIncomeIncludedArray);
    const txnsIncomeExcluded = ImmutableList(txnsIncomeExcludedArry);
    const txnsBillsIncluded = ImmutableList(txnsBillsIncludedArray);
    const txnsBillsExcluded = ImmutableList(txnsBillsExcludedArry);
    const txnsTransfersIncluded = ImmutableList(txnsTransfersIncludedArray);
    const txnsTransfersExcluded = ImmutableList(txnsTransfersExcludedArry);
    const txnsSubscriptionsIncluded = ImmutableList(txnsSubscriptionsIncludedArray);
    const txnsSubscriptionsExcluded = ImmutableList(txnsSubscriptionsExcludedArry);

    return {
      income: {
        includeData: txnsIncomeIncluded,
        excludeData: txnsIncomeExcluded,
        excludedAmount: getTxnSumForCoa(txnsIncomeExcluded, incomeCoas),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getTxnSumForCoa(txnsIncomeIncluded, incomeCoas)
            : freeToSpend.calculatedIncomeAmount || 0,
      },
      bills: {
        includeData: txnsBillsIncluded,
        excludeData: txnsBillsExcluded,
        excludedAmount: getTxnSumForCoa(txnsBillsExcluded, expenseCoas), // won't need coa exclusion when we move to txnItems
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getTxnSumForCoa(txnsBillsIncluded, expenseCoas)
            : freeToSpend.calculatedBillsAmount || 0,
      },
      transfers: {
        includeData: txnsTransfersIncluded,
        excludeData: txnsTransfersExcluded,
        excludedAmount: getTxnSumWithCallback(txnsTransfersExcluded, transferCallback),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getTxnSumWithCallback(txnsTransfersIncluded, transferCallback)
            : freeToSpend.calculatedTransferAmount || 0,
      },
      subscriptions: {
        includeData: txnsSubscriptionsIncluded,
        excludeData: txnsSubscriptionsExcluded,
        excludedAmount: getTxnSumForCoa(txnsSubscriptionsExcluded, expenseCoas),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getTxnSumForCoa(txnsSubscriptionsIncluded, expenseCoas)
            : freeToSpend.calculatedSubscriptionsAmount || 0,
      },
      goals: goalsData,
    };
  },
);

// TODO: deprecate after splits fully adopted
// This selector returns spending plan breakdown data (income, bills, transfers, subscriptions and goals).
// Either from qcs or client calculated.
export const getSpendingPlanBreakdownData = (state: State, freeToSpend: FreeToSpend) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    const startTime = performance.now();
    const calcNumber = serverBreakdownCalcNumber + 1;
    const serverBreakDown = getQCSSpendingPlanBreakdownData(state, freeToSpend);
    if (calcNumber === serverBreakdownCalcNumber) {
      // only report time if we have actually hit the combiner for recalc
      tracker.track(tracker.events.spendingPlanServerCalc, {
        calcTime: performance.now() - startTime,
        type: 'breakdown',
      });
    }
    return serverBreakDown;
  }
  const startTime = performance.now();
  const calcNumber = breakdownCalcNumber + 1;
  const clientCalc = getSpendingPlanBreakdownDataSelector(state, freeToSpend);
  if (calcNumber === breakdownCalcNumber) {
    // only report time if we have actually hit the combiner for recalc
    tracker.track(tracker.events.spendingPlanClientCalc, {
      calcTime: performance.now() - startTime,
      type: 'breakdown',
    });
  }
  return clientCalc;
};

const getSpendingPlanBreakdownDataWithSplitsSelector = createSelector(
  [
    (_state, freeToSpend: FreeToSpend) => freeToSpend,
    (state, freeToSpend: FreeToSpend) => getTransactionsForMonth(state, freeToSpend.date),
    categoriesSelectors.getIncomeCOAIds,
    transactionsSelectors.getDateOnAfter,
    (state) =>
      accountsSelectors.getLoadPending(state) ||
      categoriesSelectors.getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state) ||
      getLoadPending(state),
  ],
  (freeToSpend, transactions, incomeCoas, earliestDate, loadPending) => {
    breakdownCalcNumber += 1;
    if (loadPending) {
      return {
        income: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedIncomeAmount ?? 0,
        },
        bills: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedBillsAmount ?? 0,
        },
        transfers: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedTransferAmount ?? 0,
        },
        subscriptions: {
          includeData: emptyItemsList,
          excludeData: emptyItemsList,
          excludedAmount: 0,
          includedAmount: freeToSpend.calculatedSubscriptionsAmount ?? 0,
        },
      };
    }
    const txnsIncomeIncludedArray: TransactionItem[] = [];
    const txnsIncomeExcludedArray: TransactionItem[] = [];
    const txnsBillsIncludedArray: TransactionItem[] = [];
    const txnsBillsExcludedArray: TransactionItem[] = [];
    const txnsTransfersIncludedArray: TransactionItem[] = [];
    const txnsTransfersExcludedArray: TransactionItem[] = [];
    const txnsSubscriptionsIncludedArray: TransactionItem[] = [];
    const txnsSubscriptionsExcludedArray: TransactionItem[] = [];

    const bucketizeTxnItem = (txnItem: TransactionItem) => {
      const { transaction, item, splitItem } = txnItem;
      const isTransfer = transactionsUtils.isTransferOrCreditCardPaymentTxn(splitItem || transaction);
      if (isTransfer && transaction.subtype !== 'GOAL_SPENT') {
        // ===== TRANSFER ==========
        if (!transaction.isExcludedFromF2S) {
          txnsTransfersIncludedArray.push(txnItem);
        } else {
          txnsTransfersExcludedArray.push(txnItem);
        }
      } else if (incomeCoas.has(item?.coa?.id as string)) {
        // ===== INCOME =====
        if (!transaction.isExcludedFromF2S) {
          txnsIncomeIncludedArray.push(txnItem);
        } else {
          txnsIncomeExcludedArray.push(txnItem);
        }
      } else if (transaction.isBill) {
        // ==== BILL ======
        if (!transaction.isExcludedFromF2S) {
          txnsBillsIncludedArray.push(txnItem);
        } else {
          txnsBillsExcludedArray.push(txnItem);
        }
      } else if (transaction.isSubscription) {
        // ===== SUBSCRIPTION =======
        if (!transaction.isExcludedFromF2S) {
          txnsSubscriptionsIncludedArray.push(txnItem);
        } else {
          txnsSubscriptionsExcludedArray.push(txnItem);
        }
      }
    };

    transactions.forEach((txn) => {
      if (transactionsUtils.isSplitTxn(txn)) {
        txn.split?.items?.forEach((splitItem) => {
          const transactionItem = mkTransactionItem({
            transaction: txn,
            splitItem: splitItem as RecordOf<SplitItemProps>,
            item: {
              ...txn.toObject(),
              ...(Record.isRecord(splitItem) ? splitItem?.toObject() : splitItem),
            },
          });
          bucketizeTxnItem(transactionItem);
        });
      } else {
        const transactionItem = mkTransactionItem({
          transaction: txn,
          item: txn.toObject(),
        });
        bucketizeTxnItem(transactionItem);
      }
    });

    const txnsIncomeIncluded = ImmutableList(txnsIncomeIncludedArray);
    const txnsIncomeExcluded = ImmutableList(txnsIncomeExcludedArray);
    const txnsBillsIncluded = ImmutableList(txnsBillsIncludedArray);
    const txnsBillsExcluded = ImmutableList(txnsBillsExcludedArray);
    const txnsTransfersIncluded = ImmutableList(txnsTransfersIncludedArray);
    const txnsTransfersExcluded = ImmutableList(txnsTransfersExcludedArray);
    const txnsSubscriptionsIncluded = ImmutableList(txnsSubscriptionsIncludedArray);
    const txnsSubscriptionsExcluded = ImmutableList(txnsSubscriptionsExcludedArray);

    return {
      income: {
        includeData: txnsIncomeIncluded,
        excludeData: txnsIncomeExcluded,
        excludedAmount: getItemsSum(txnsIncomeExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getItemsSum(txnsIncomeIncludedArray)
            : freeToSpend.calculatedIncomeAmount || 0,
      },
      bills: {
        includeData: txnsBillsIncluded,
        excludeData: txnsBillsExcluded,
        excludedAmount: getItemsSum(txnsBillsExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getItemsSum(txnsBillsIncludedArray)
            : freeToSpend.calculatedBillsAmount || 0,
      },
      transfers: {
        includeData: txnsTransfersIncluded,
        excludeData: txnsTransfersExcluded,
        excludedAmount: getItemsSum(txnsTransfersExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getItemsSum(txnsTransfersIncludedArray)
            : freeToSpend.calculatedTransferAmount || 0,
      },
      subscriptions: {
        includeData: txnsSubscriptionsIncluded,
        excludeData: txnsSubscriptionsExcluded,
        excludedAmount: getItemsSum(txnsSubscriptionsExcludedArray),
        includedAmount:
          !earliestDate || earliestDate < freeToSpend.date
            ? getItemsSum(txnsSubscriptionsIncludedArray)
            : freeToSpend.calculatedSubscriptionsAmount || 0,
      },
    };
  },
);

// returns txnItems instead of txnRecords in breakdownLists
export const getSpendingPlanBreakdownDataWithSplits = (state: State, freeToSpend: FreeToSpend) => {
  const startTime = performance.now();
  const calcNumber = breakdownCalcNumber + 1;
  const clientCalc = getSpendingPlanBreakdownDataWithSplitsSelector(state, freeToSpend);
  if (calcNumber === breakdownCalcNumber) {
    // only report time if we have actually hit the combiner for recalc
    tracker.track(tracker.events.spendingPlanClientCalc, {
      calcTime: performance.now() - startTime,
      type: 'breakdown',
    });
  }
  return clientCalc;
};

export const getIncomeAfterBillsAndSavings = (state: State, freeToSpend: FreeToSpend) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    return freeToSpend.totalToSpendAmount;
  }
  return getIncomeAfterBillsAndSavingsSelector(state, freeToSpend);
};

function isNumber(value: any) {
  return typeof value === 'number';
}

export const getIncomeAfterBillsAndSavingsSelector = createSelector(
  [
    (_state, freeToSpend: FreeToSpend) => freeToSpend,
    categoriesSelectors.getIncomeCOAIds,
    transactionsSelectors.getDateOnAfter,
    (state, freeToSpend: FreeToSpend) => getTransactionsForMonth(state, freeToSpend.date),
    getSpendingPlanGoalsBreakdownData,
    (state) =>
      accountsSelectors.getLoadPending(state) ||
      categoriesSelectors.getLoadPending(state) ||
      transactionsSelectors.getLoadPending(state) ||
      getLoadPending(state),
  ],
  (freeToSpend, incomeCoas, earliestDate, transactions: TxnList, goalsData, loadPending) => {
    if (loadPending || (earliestDate && earliestDate > freeToSpend.date)) {
      return freeToSpend.totalToSpendAmount ?? 0;
    }

    let incomeAmount = 0;
    let billsAmount = 0;
    let transfersAmount = 0;
    let subscriptionsAmount = 0;
    // TODO: Can this be done in forEach below?
    let { includedAmount: goalsAmount } = goalsData;
    // First, filter down the list to what we need.
    const ftsTransactions = transactions?.filter((txn) => !txn.isExcludedFromF2S);

    // I think this is working now - just check the bucketize/breakdown up above

    const bucketizeTxnItem = (txn: TransactionRecord, splitItem?: SplitItemProps) => {
      const dataSource = splitItem ? splitItem : txn;
      const isTransfer = transactionsUtils.isTransferOrCreditCardPaymentTxn(dataSource);
      if (incomeCoas.has(dataSource?.coa?.id as string)) {
        // ===== INCOME =====
        incomeAmount += dataSource.amount || 0;
      } else if (txn.isBill && !isTransfer) {
        // ==== BILL ======
        billsAmount += dataSource.amount || 0;
      } else if (txn.isSubscription && !isTransfer) {
        // ===== SUBSCRIPTION =======
        subscriptionsAmount += dataSource.amount || 0;
      } else if (isTransfer && txn.subtype !== 'GOAL_SPENT') {
        // ===== TRANSFER ==========
        transfersAmount += dataSource.amount || 0;
      }
    };

    ftsTransactions?.forEach((txn) => {
      if (transactionsUtils.isSplitTxn(txn)) {
        txn.split?.items?.forEach((item) => bucketizeTxnItem(txn, item));
      } else {
        bucketizeTxnItem(txn);
      }
    });

    // Check overwrites and use them.
    if (!incomeAmount || isNumber(freeToSpend.overwrittenIncomeAmount)) {
      incomeAmount = freeToSpend.overwrittenIncomeAmount ?? freeToSpend.calculatedIncomeAmount ?? 0;
    }
    if (!billsAmount || isNumber(freeToSpend.overwrittenBillsAmount)) {
      billsAmount = freeToSpend.overwrittenBillsAmount ?? freeToSpend.calculatedBillsAmount ?? 0;
    }
    if (!transfersAmount || isNumber(freeToSpend.overwrittenTransferAmount)) {
      transfersAmount = freeToSpend.overwrittenTransferAmount ?? freeToSpend.calculatedTransferAmount ?? 0;
    }
    if (!subscriptionsAmount || isNumber(freeToSpend.overwrittenSubscriptionsAmount)) {
      subscriptionsAmount =
        freeToSpend.overwrittenSubscriptionsAmount ?? freeToSpend.calculatedSubscriptionsAmount ?? 0;
    }
    if (!goalsAmount || isNumber(freeToSpend.overwrittenGoalsAmount)) {
      goalsAmount = freeToSpend.overwrittenGoalsAmount ?? freeToSpend.calculatedGoalsAmount ?? 0;
    }
    return roundOff(incomeAmount + billsAmount + transfersAmount + subscriptionsAmount + goalsAmount, 2);
  },
);

export const getSpentAmount = (state: State, freeToSpend: FreeToSpend) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    return freeToSpend.spentAmount;
  }
  return getSpentAmountSelector(state, freeToSpend);
};

export const getSpentAmountSelector = createSelector(
  [
    (state, freeToSpend: FreeToSpend) => getOtherSpendingTxns(state, freeToSpend.id ?? ''),
    (state, freeToSpend: FreeToSpend) => getPlannedCoaIds(state, freeToSpend.date),
    categoriesSelectors.getIncomeCOAIds,
    (_state, freeToSpend: FreeToSpend) => freeToSpend.spentAmount,
  ],
  (spent, plannedCoas, incomeCoas, calculatedSpentAmount) => {
    return (
      getTxnSumWithCallback?.(
        spent.chartTxns,
        (item: TransactionRecord) =>
          transferAndCoaCallback(item, Set([...plannedCoas, ...incomeCoas].filter((id): id is string => !!id))),
        false,
      ) ??
      calculatedSpentAmount ??
      0
    );
  },
);

let leftToSpendCalcNumber = 0;
export const getLeftToSpendSelector = createSelector(
  [
    getIncomeAfterBillsAndSavings,
    getPlannedSpendingAmount,
    getSpentAmount,
    (_state, freeToSpend: FreeToSpend) => freeToSpend.leftToSpendAmount,
  ],
  (incomeAfterBillsAndSavings, plannedSpendingAmount, spentAmount) => {
    leftToSpendCalcNumber += 1;
    return (incomeAfterBillsAndSavings ?? 0) + (plannedSpendingAmount ?? 0) + (spentAmount ?? 0);
  },
);

export const getLeftToSpend = (state: State, freeToSpend: FreeToSpend) => {
  const spendingPlanClientCalc = featureFlagsSelectors.getFeatureFlag(state, 'spendingPlanClientCalc');
  if (!spendingPlanClientCalc) {
    return freeToSpend.leftToSpendAmount;
  }
  const startTime = performance.now();
  const calcNumber = leftToSpendCalcNumber + 1;
  const clientCalc = getLeftToSpendSelector(state, freeToSpend);
  if (calcNumber === leftToSpendCalcNumber) {
    // only report time if we have actually hit the combiner for recalc
    tracker.track(tracker.events.spendingPlanClientCalc, {
      calcTime: performance.now() - startTime,
      type: 'leftToSpend',
    });
  }
  return clientCalc;
};

export type COANodeProps = {
  id: string;
  parentId: string;
  coas: Set<chartOfAccountsTypes.ChartOfAccountProps>;
  transactions: ImmutableMap<string, TransactionRecord>;
  balance: number;
  label?: string | undefined;
  logoUrl?: string | undefined;
};
export type COANode = RecordOf<COANodeProps>;
const mkCOANode = Record<COANodeProps>({
  id: '0',
  parentId: '0',
  coas: Set<chartOfAccountsTypes.ChartOfAccountProps>(),
  transactions: ImmutableMap<string, TransactionRecord>(),
  balance: 0,
  label: undefined,
  logoUrl: undefined,
});
export type NodeMap = OrderedMap<string, COANodeProps>;
const emptyNodes = OrderedMap<string, COANodeProps>();

const updateNodeWithTransaction = (
  coaNodesTree: any,
  id: string,
  parentId: string,
  label: string | undefined,
  logoUrl: string | undefined,
  transaction:
    | {
        id: string | null | undefined;
        split: any | null | undefined;
        coa: any | null | undefined;
        amount: number | null | undefined;
        postedOn: string | null | undefined;
      }
    | SplitItem,
  amount: number,
  coa?: chartOfAccountsTypes.ChartOfAccountProps,
) => {
  // eslint-disable-next-line no-undef
  if (assert(id, 'fts node id should be defined')) {
    const coaNode =
      coaNodesTree.get(id) ||
      new mkCOANode({
        id,
        parentId,
        label,
        logoUrl,
      });
    const updatedNode = coaNode.merge({
      transactions: coaNode.transactions.set(transaction.id, transaction),
      balance: coaNode.balance + Number(amount),
      coas: coaNode.coas.add(coa),
      logoUrl,
    });
    coaNodesTree.set(id, updatedNode);
  }
};

export const getNodesFromTransactions = createSelector(
  [
    categoriesSelectors.getCategoriesById,
    accountsSelectors.getAccountsById,
    selectorsHelpers.createTrimSelector(
      // TrimSelector overwrites this state type to 'unknown', typing it State fixes it.
      (state: State, transactions) => transactions || transactionsSelectors.getTransactionsById(state),
      (transaction: TransactionRecord) => ({
        id: transaction.id,
        split: transaction.split,
        coa: transaction.coa,
        amount: transaction.amount,
        postedOn: transaction.postedOn,
      }),
    ),
    (_state, _transactions, maxNodesNum: number) => maxNodesNum,
    selectorsHelpers.createValueCache(
      (state, _transactions, _maxNodesNum, date) => getPlannedSpendingCoas(state, date, false) || null,
    ),
  ],
  (categoriesById, accountsById, transactionsById, maxNodesNum, plannedCoas) => {
    if (!transactionsById || transactionsById.size === 0) {
      return emptyNodes;
    }
    const coaNodesRow = OrderedMap<string, COANodeProps>()
      .withMutations((coaNodes) => {
        transactionsById.forEach((transaction) => {
          const isSplit = transactionsUtils.isSplitTxn(transaction);
          const items = (isSplit ? transaction?.split?.items : [transaction]) as {
            amount: number;
            coa: chartOfAccountsTypes.ChartOfAccountProps;
          }[];
          items?.forEach((item) => {
            let id = chartOfAccountsUtils.getCoaId(item.coa);
            if (item.coa && item.coa.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT) {
              const account = accountsById.get(id);
              const label = account?.name || 'Unknown';
              // add node for account-specific transfer
              updateNodeWithTransaction(
                coaNodes,
                `${id}-explicit`,
                id,
                label,
                undefined,
                transaction,
                item.amount,
                item.coa,
              );
              // update baseline 'transfer' bucket
              updateNodeWithTransaction(
                coaNodes,
                id,
                'BALANCE_ADJUSTMENT_2',
                label,
                undefined,
                transaction,
                item.amount,
                item.coa,
              );
              updateNodeWithTransaction(
                coaNodes,
                'BALANCE_ADJUSTMENT_2',
                '0',
                'Transfer',
                undefined,
                transaction,
                item.amount,
                item.coa,
              );
            } else if (item.coa && item.coa.type === chartOfAccountsTypes.CoaTypeEnum.CATEGORY) {
              let category = categoriesById.get(id);
              if (category?.type === categoriesTypes.CategoryTypeEnum.INCOME || plannedCoas?.has(id)) {
                return;
              }
              let label = (category && category.name) || 'Unknown';
              const logoUrl = category && category.logoUrl;
              const idExplicit = `${id}-explicit`;
              // add value to base COA
              updateNodeWithTransaction(coaNodes, idExplicit, id, label, logoUrl, transaction, item.amount, item.coa);

              // add value to parent COAs
              for (let loopProtect = 0; loopProtect < 255 && id !== '0'; loopProtect++) {
                category = categoriesById.get(id);
                const parentId = category?.parentId || '0';
                label = (category && category.name) || 'Unknown';
                updateNodeWithTransaction(coaNodes, id, parentId, label, logoUrl, transaction, item.amount, item.coa);
                id = parentId;
              }
            } else if (!transactionsUtils.isTransferOrCreditCardPaymentTxn(item)) {
              const label = chartOfAccountsSelectors.getCoaStringSelector(undefined, item.coa, true);
              updateNodeWithTransaction(coaNodes, id, '0', label, undefined, transaction, item.amount, item.coa);
            }
            updateNodeWithTransaction(coaNodes, '0', '', 'All', undefined, transaction, item.amount, item.coa); // update root
          });
        });
      })
      .groupBy((node: any) => node.transactions.hashCode())
      .flatMap((coaNodes: any) =>
        coaNodes.filter(
          (node: any) =>
            coaNodes.size === 1 ||
            node.parentId === '0' ||
            coaNodes.find((childNode: any) => childNode.parentId === node.id),
        ),
      ); // de-dup orphaned leaves

    // apply maxNodesNum rule
    const coaNodesTrimmed = OrderedMap<string, COANode>().withMutations((coaNodes) => {
      const coaNodesByParentId = coaNodesRow.groupBy((node: any) => node.parentId);
      coaNodesByParentId.forEach((coaNodesGroup, parentId) => {
        const coaNodesGroupSorted: any = coaNodesGroup.sort((node1: any, node2: any) =>
          node1.balance !== node2.balance ? node2.balance - node1.balance : node1.label.localeCompare(node2.label),
        );
        if (coaNodesGroupSorted.size > maxNodesNum) {
          const nodesToKeep = coaNodesGroupSorted.takeLast(maxNodesNum - 1);
          const otherId = `${parentId}-others`;
          const otherNodes = coaNodesGroupSorted
            .skipLast(maxNodesNum - 1)
            .map((node: COANode) => node.set('parentId', otherId));
          const otherBalance = otherNodes.reduce((balance: number, node: any) => balance + node.balance, 0);
          const otherTransactions = otherNodes.flatMap((nodeEntry: any) => nodeEntry.transactions);
          const otherCOAs = otherNodes.reduce((coas: any, node: any) => coas.merge(node.coas), Set());
          const otherNode = new mkCOANode({
            id: otherId,
            parentId,
            transactions: otherTransactions,
            balance: otherBalance,
            label: 'Other',
            coas: otherCOAs,
          });
          coaNodes.set(otherId, otherNode);
          coaNodes.merge(nodesToKeep);

          coaNodes.merge(otherNodes);
        } else {
          coaNodes.merge(coaNodesGroupSorted);
        }
      });
    });
    return coaNodesTrimmed;
  },
);

export const childNodes = createCachedSelector(
  (allNodes: NodeMap) => allNodes,
  (_allNodes: unknown, nodeId: string) => nodeId,
  (allNodes, nodeId) => allNodes && allNodes.filter((node: COANodeProps) => node.parentId === nodeId),
)({
  keySelector: (allNodes: NodeMap, nodeId: string) => `${allNodes?.hashCode()}:${nodeId}`,
  cacheObject: new LruObjectCache({ cacheSize: 22 }), // need larger cache as this is called for each node in the graph
});

export const cachedNodesFromNode = createCachedSelector(
  (node?: COANodeProps) => node,
  (node?: COANodeProps) => (node?.id ? OrderedMap([[node.id, node]]) : undefined),
)({
  keySelector: (node) => `${node?.id}`,
  cacheObject: new LruObjectCache({ cacheSize: 4 }),
});

export const coasForNodes = createSelectorBase(
  (nodes?: NodeMap) => nodes,
  (nodes) =>
    Set<chartOfAccountsTypes.ChartOfAccountProps>().withMutations(
      (coas) => nodes && nodes.reduce((coasAccumulator, node) => coasAccumulator.merge(node.coas), coas),
    ),
);

export const pathToNode = createSelectorBase(
  (allNodes: NodeMap) => allNodes,
  (_allNodes: unknown, nodeId: string) => nodeId,
  (allNodes, nodeId) =>
    allNodes &&
    nodeId &&
    ImmutableList().withMutations((nodePath) => {
      for (let node = allNodes.get(nodeId); node; node = allNodes.get(node.parentId)) {
        nodePath.unshift(node);
      }
    }),
);

export const mergeCollections = createCachedSelector(
  (collectionArray: RecordOf<any>[]) => collectionArray,
  (collectionArray) => {
    const collectionArrayNormalized = collectionArray.filter((collection) => collection);
    return collectionArrayNormalized && collectionArrayNormalized.length
      ? merge(collectionArrayNormalized[0], ...collectionArrayNormalized.slice(1))
      : undefined;
  },
)({
  keySelector: (collectionArray) => collectionArray?.map((collection) => collection?.hashCode?.()).join(':') ?? 'none',
  cacheObject: new LruObjectCache({ cacheSize: 10 }),
  selectorCreator: selectorsHelpers.createArraysEqualShallowSelector,
});

// note - the reason we are using getSortedTransactionsByIds is due to the fact that this is the same selector used in planBreakdown, so the cache is already saved and
// we don't have to do anymore expensive calculations
export const closeOutPriorMonth = createSelector(
  [
    // TODO: this still uses QCS txnIDs for lookup - ideally move to client-side evaluation (like re-using breakdownData)
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.incomeTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.billsTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.subscriptionsTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.spentTxnIds),
    (state, freeToSpend: FreeToSpend) =>
      transactionsSelectors.getSortedTransactionsByIds(state, freeToSpend?.transferTxnIds),
    (state, freeToSpend: FreeToSpend) => getCalculatedPlannedSpendingObject(state, freeToSpend.date),
    (_state, freeToSpend: FreeToSpend) => freeToSpend,
  ],
  (incomeTxns, billTxns, subTxns, spentTxns, transferTxns, calculatedSpendingInfo, freeToSpend) => {
    let hasPendingTxns = false;
    const filterTxns = (txn: TransactionRecord) => {
      if (
        !hasPendingTxns &&
        txn.state === 'PENDING' &&
        txn.source !== 'SCHEDULED_TRANSACTION_PENDING' &&
        !txn.stModelId
      ) {
        hasPendingTxns = true;
      }
      return txn.source === 'SCHEDULED_TRANSACTION_PENDING';
    };
    const incomeLength = incomeTxns?.filter(filterTxns)?.size || 0;
    const billsLength = billTxns?.filter(filterTxns)?.size || 0;
    const subsLength = subTxns?.filter(filterTxns)?.size || 0;
    const transfersLength = transferTxns?.filter(filterTxns)?.size || 0;
    // this is for setting hasPendingTxns to true if they exist in spent
    spentTxns?.forEach(filterTxns);
    const plannedZeroArr: PlannedSpendingItem[] = [];
    const plannedArr = freeToSpend?.plannedSpendingItems?.filter((item) => {
      const calculatedIncluded = calculatedSpendingInfo[item.id]?.calculatedIncluded;
      if (
        Math.abs(calculatedIncluded || 0) === Math.abs(item.overwrittenTargetAmount ?? item.targetAmount) &&
        !validRolloverStates.includes(item?.rolloverType)
      ) {
        plannedZeroArr.push(item);
      }
      return (
        Math.abs(calculatedIncluded || 0) <= Math.abs(item.overwrittenTargetAmount ?? item.targetAmount) &&
        !validRolloverStates.includes(item?.rolloverType)
      );
    });
    const incomeAfterWarning = billsLength !== 0 || incomeLength !== 0 || subsLength !== 0 || transfersLength !== 0;

    return {
      incomeLength,
      billsLength,
      subsLength,
      plannedArr,
      plannedZeroArr,
      freeToSpendId: freeToSpend?.id,
      hasPendingTxns,
      incomeAfterWarning,
    };
  },
);

export const getPastFreeToSpends = createSelector(
  getResourcesById,
  dateSelectors.getCurrentDateString,
  (resources, currentDate) =>
    resources.filter((f2s) => {
      const today = DateTime.fromISO(currentDate).startOf('month');
      return DateTime.fromISO(f2s.date) < today;
    }),
);

export const getFutureFreeToSpendsIds = createSelector(
  getResourcesById,
  (_state, props: { startDate: string }) => props.startDate,
  (resources: ImmutableMap<string, FreeToSpend>, startDate) =>
    resources
      .filter((f2s) => {
        const today = DateTime.fromISO(startDate).startOf('month');
        return DateTime.fromISO(f2s.date) >= today;
      })
      .toList()
      .toArray()
      .map((f2s) => f2s.id),
);

export const getPastFreeToSpendAmounts = createSelector(
  [getPastFreeToSpends, (_state, endDate?: string) => endDate],
  (freeToSpends, endDate) => {
    const retArr: any[] = [];
    freeToSpends.forEach((f2s) => {
      const F2Sdate = DateTime.fromISO(f2s.date as string) as DateTime;
      if (!endDate || F2Sdate <= DateTime.fromISO(endDate)) {
        retArr.push({
          date: f2s.date,
          total: (f2s.spentAmount || 0) * -1,
          txns: undefined,
          category: F2Sdate.toFormat('LLL'),
          custom: F2Sdate.toFormat('LLL yyyy'),
        });
      }
      return true;
    });
    return retArr.sort((a, b) => (DateTime.fromISO(a.date) < DateTime.fromISO(b.date) ? -1 : 1));
  },
);

export const getAverageSpentFromF2S = createSelector(
  [
    (state, _months: number, endDate: string) => getPastFreeToSpendAmounts(state, endDate),
    (_state, months: number) => months,
  ],
  (pastFreeToSpends, months) => {
    if (pastFreeToSpends?.length > 0) {
      let max = Number.NEGATIVE_INFINITY;
      let min = 0;
      const sum = pastFreeToSpends.slice(pastFreeToSpends.length - months).reduce((accumulator, value: any) => {
        if (max < value.total) {
          max = value.total;
        }
        if (min > value.total) {
          min = value.total;
        }
        return accumulator + value.total;
      }, 0);
      return {
        average: sum / months,
        max,
        min,
        sum,
      };
    }
    return { average: 0, max: 0, sum: 0 };
  },
);

export const getProjectionType = (freeToSpend: FreeToSpend) => freeToSpend.projectionType || 'no-project';

export const getBuffer = (freeToSpend: FreeToSpend) => freeToSpend.projectionBuffer || null;

export const getProjectedOtherSpending = (freeToSpend: FreeToSpend) => freeToSpend.projectedOtherSpending || 0;

// ========== SPENDING PLAN SELECTORS END HERE =============
